import { LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS } from "./linkedInLeadFormProfileFields.js";

export const LINKEDIN_LEAD_FORM_PREVIEW_PROFILE_LIMIT = 2;

const readText = (value) => (typeof value === "string" ? value.trim() : "");

const hasText = (value) => readText(value).length > 0;

const hasItems = (value) => Array.isArray(value) && value.length > 0;

const hasLeadFormPreviewDetail = (detail) =>
  hasText(detail?.headline) ||
  hasText(detail?.description) ||
  hasItems(detail?.profileFields);

export const readLinkedInLeadFormPreviewSource = (form) => {
  if (!form || typeof form !== "object") return null;
  const detail =
    form.duplicateValues && typeof form.duplicateValues === "object"
      ? form.duplicateValues
      : form;
  if (!hasLeadFormPreviewDetail(detail)) return null;
  return detail;
};

const readProfileFieldKey = (item) => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return "";
  return readText(item.value || item.predefinedField || item.type);
};

export const pickLinkedInLeadFormPreviewStorageFields = (source) => {
  const detail = readLinkedInLeadFormPreviewSource(source);
  if (!detail) return {};
  const allowed = new Set(
    LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS.map((option) => option.value)
  );
  const headline = readText(detail.headline);
  const description = readText(detail.description);
  const profileFields = (
    Array.isArray(detail.profileFields) ? detail.profileFields : []
  )
    .map(readProfileFieldKey)
    .filter((value) => value && allowed.has(value));
  const stored = {};
  if (headline) stored.headline = headline;
  if (description) stored.description = description;
  if (profileFields.length) stored.profileFields = profileFields;
  return stored;
};

export const mergeLinkedInLeadFormPreviewStorageFields = (
  form,
  recommendation
) => ({
  ...pickLinkedInLeadFormPreviewStorageFields(recommendation),
  ...pickLinkedInLeadFormPreviewStorageFields(form),
});

export const matchesRecommendedLinkedInLeadForm = (form, recommendation) => {
  if (!form?.id || !recommendation) return false;
  if (
    recommendation.id != null &&
    String(recommendation.id) === String(form.id)
  ) {
    return true;
  }
  if (recommendation.name && form.name && recommendation.name === form.name) {
    return true;
  }
  return false;
};

export const resolveLinkedInLeadFormPreviewSource = (form, recommendation) => {
  const fromForm = readLinkedInLeadFormPreviewSource(form);
  if (fromForm) return fromForm;
  if (!matchesRecommendedLinkedInLeadForm(form, recommendation)) return null;
  return readLinkedInLeadFormPreviewSource(recommendation);
};

const collectProfileFields = (source) => {
  const selected = new Set(
    (Array.isArray(source?.profileFields) ? source.profileFields : [])
      .map(readProfileFieldKey)
      .filter(Boolean)
  );
  return LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS.filter((option) =>
    selected.has(option.value)
  ).map((option) => ({ labelKey: option.label }));
};

export const buildLinkedInLeadFormPreview = (
  source,
  { profileLimit = LINKEDIN_LEAD_FORM_PREVIEW_PROFILE_LIMIT } = {}
) => {
  const profileFields = collectProfileFields(source);
  const visibleLimit =
    Number.isFinite(profileLimit) && profileLimit > 0 ? profileLimit : 0;
  return {
    title: readText(source?.headline),
    description: readText(source?.description),
    contactFields: profileFields.slice(0, visibleLimit),
    moreCount: Math.max(0, profileFields.length - visibleLimit),
  };
};

export const hasLinkedInLeadFormPreviewContent = (preview) =>
  Boolean(
    preview?.title ||
      preview?.description ||
      preview?.contactFields?.length ||
      preview?.moreCount > 0
  );
